import { Spin } from 'antd';
import { useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { useOptionalItems } from '~/api/easy-estimator-api/optional-items-api/useOptionalItems';
import { IMAGE_COMING_SOON } from '~/core/constants';
import { ProductType } from '~/core/enums';
import { isCustomWorkProduct, isWarrantyProduct } from '~/core/helpers';
import { OptionalProduct } from '~/core/models';
import { getProductDescription, imgUrl } from '~/core/utils';
import { useAppDispatch, useAppSelector } from '~/redux/hooks';
import {
  type CustomLaborData,
  type ProductId,
  addOneOptionalItem,
  clearSelectedWarrantyProduct,
  deselectOptionalItem,
  removeOneOptionalItem,
  resetCustomLaborOptionalItemsForProduct,
  selectPickedCustomLaborOptionalItems,
  selectSelectedOptionalItems,
  selectSelectedWarrantyProductId,
  selectWarrantyProduct,
  setCustomLaborOptionalItemsForProduct,
  setOptionalItemQuantity,
} from '~/redux/slices/optionalItems';
import DesignStepContainer from '~molecules/DesignStepContainer';
import ApplianceCard, {
  type ApplianceCardProps,
} from '~organisms/ApplianceCard';
import CustomLaborModal from '~organisms/CustomLaborModal/';
import DesignSectionHeader from '~organisms/DesignSectionHeader';
import * as St from '~organisms/SidePanelAddOnOptionsTab/SidePanelAddOnOptionsTab.styles';
import WarrantyDetailsModal from '~organisms/WarrantyDetailsModal';

const SidePanelAddOnOptionsTab = () => {
  const dispatch = useAppDispatch();
  const { t } = useTranslation();
  const { visibleOptionalItems: optionalItems, isFetchingOptionalItems } =
    useOptionalItems();
  const selectedOptionalItems = useAppSelector(selectSelectedOptionalItems);
  const selectedWarrantyId = useAppSelector(selectSelectedWarrantyProductId);
  const selectedCustomLaborItems = useAppSelector(
    selectPickedCustomLaborOptionalItems
  );
  const [warrantyCandidateId, setWarrantyCandidateId] = useState<
    ProductId | undefined
  >();
  const [selectedCustomLabor, setSelectedCustomLabor] = useState<
    OptionalProduct | undefined
  >();
  const [warrantyProducts, customWorkProducts, optionalProducts] = useMemo(
    () =>
      optionalItems.reduce<
        [OptionalProduct[], OptionalProduct[], OptionalProduct[]]
      >(
        ([warranties, customWorks, others], item) =>
          isWarrantyProduct(item)
            ? [[...warranties, item], customWorks, others]
            : isCustomWorkProduct(item)
              ? [warranties, [...customWorks, item], others]
              : [warranties, customWorks, [...others, item]],
        [[], [], []]
      ),
    [optionalItems]
  );
  const [selectedWarranty, warrantyCandidate] = useMemo(
    () =>
      warrantyProducts.reduce<
        [OptionalProduct | undefined, OptionalProduct | undefined]
      >(
        ([selected, candidate], item) =>
          !selected && (selectedWarrantyId ?? 0) === item.productId
            ? [item, candidate]
            : !candidate && (warrantyCandidateId ?? 0) === item.productId
              ? [selected, item]
              : [selected, candidate],
        [undefined, undefined]
      ),
    [selectedWarrantyId, warrantyCandidateId, warrantyProducts]
  );
  const focusedWarrantyId = useMemo(
    () => selectedWarrantyId ?? warrantyCandidateId,
    [selectedWarrantyId, warrantyCandidateId]
  );
  const isChangingSelectedWarranty = Boolean(
    selectedWarrantyId &&
      warrantyCandidateId &&
      selectedWarrantyId !== warrantyCandidateId
  );

  const clearWarrantyCandidate = useCallback(
    () => setWarrantyCandidateId(undefined),
    []
  );
  const selectWarrantyCandidate = useCallback(() => {
    if (warrantyCandidateId) {
      dispatch(selectWarrantyProduct(warrantyCandidateId));
    }
    clearWarrantyCandidate();
  }, [clearWarrantyCandidate, dispatch, warrantyCandidateId]);
  const addCustomLabor = useCallback(
    (customLaborOptionalItems: CustomLaborData[]) => {
      const productId = selectedCustomLabor?.productId;

      if (productId) {
        if (customLaborOptionalItems.length > 0) {
          dispatch(
            setCustomLaborOptionalItemsForProduct({
              productId,
              customLaborOptionalItems,
            })
          );
        } else {
          dispatch(resetCustomLaborOptionalItemsForProduct(productId));
        }
      }

      setSelectedCustomLabor(undefined);
    },
    [dispatch, selectedCustomLabor?.productId]
  );
  const makeApplianceCardProps = (
    item: OptionalProduct
  ): ApplianceCardProps => ({
    image: imgUrl(item.imageUrl ?? IMAGE_COMING_SOON, 300),
    title: getProductDescription(item),
    showPriceIfZero: false,
    priceWithoutDiscount: item.price,
    unitOfMeasure: item.unitOfMeasure?.toLowerCase(),
  });

  const getCustomLaborItemsQuantity = (customLaborItems: CustomLaborData[]) =>
    customLaborItems.reduce(
      (quantity, customLaborItem) => quantity + customLaborItem.quantity,
      0
    ) || undefined;

  return (
    <>
      <DesignSectionHeader
        title={t('SCROLLABLE_AREA.OPTIONAL_PRODUCT.TITLE')}
        description={t('SCROLLABLE_AREA.OPTIONAL_PRODUCT.DESCRIPTION')}
      />

      {isFetchingOptionalItems ? (
        <Spin style={{ fontSize: 24, marginLeft: '40%' }} />
      ) : (
        <DesignStepContainer>
          {warrantyProducts.map((item) => (
            <ApplianceCard
              {...makeApplianceCardProps(item)}
              key={item.productId}
              isSelected={item.productId === focusedWarrantyId}
              onClick={() => {
                if (selectedWarrantyId === item.productId) {
                  dispatch(clearSelectedWarrantyProduct());
                } else {
                  setWarrantyCandidateId(item.productId);
                }
              }}
              applianceType={ProductType.MODIFY_ON}
            />
          ))}

          {customWorkProducts.map((item) => {
            const quantity = getCustomLaborItemsQuantity(
              selectedCustomLaborItems[item.productId] || []
            );

            const openCustomLabor = () => setSelectedCustomLabor(item);

            return (
              <ApplianceCard
                {...makeApplianceCardProps(item)}
                key={item.productId}
                isSelected={
                  selectedCustomLaborItems[item.productId]?.length > 0
                }
                onClick={openCustomLabor}
                applianceType={ProductType.OPTIONAL_PRODUCT}
                counterProps={{
                  quantity,
                  // Pass undefined to onIncrease hide the +Add button when quantity > 0.
                  onIncrease: (quantity ?? 0) > 0 ? undefined : openCustomLabor,
                }}
              />
            );
          })}

          {optionalProducts.map((item) => {
            const itemQty = selectedOptionalItems[item.productId] ?? 0;
            const isSelected = itemQty > 0;

            return (
              <ApplianceCard
                {...makeApplianceCardProps(item)}
                key={item.productId}
                isSelected={isSelected}
                onClick={() => {
                  if (isSelected) {
                    dispatch(deselectOptionalItem(item.productId));
                  } else {
                    dispatch(addOneOptionalItem(item.productId));
                  }
                }}
                counterProps={{
                  quantity: isSelected ? itemQty : undefined,
                  onIncrease: () =>
                    dispatch(addOneOptionalItem(item.productId)),
                  onDecrease: () =>
                    dispatch(removeOneOptionalItem(item.productId)),
                  onUpdateValue: (quantity) =>
                    dispatch(
                      setOptionalItemQuantity({
                        productId: item.productId,
                        quantity,
                      })
                    ),
                }}
                applianceType={ProductType.OPTIONAL_PRODUCT}
              />
            );
          })}
        </DesignStepContainer>
      )}

      <CustomLaborModal
        customLabor={selectedCustomLabor}
        onConfirm={addCustomLabor}
        onCancel={() => setSelectedCustomLabor(undefined)}
      />

      <WarrantyDetailsModal
        onClose={clearWarrantyCandidate}
        onConfirm={selectWarrantyCandidate}
        warrantyCandidate={
          isChangingSelectedWarranty ? undefined : warrantyCandidate
        }
      />

      <St.WarrantyWarningModal
        isVisible={isChangingSelectedWarranty}
        onCancel={clearWarrantyCandidate}
        onConfirm={() => dispatch(clearSelectedWarrantyProduct())}
        title={`Change to ${warrantyCandidate?.name}`}
        description={`By selecting ${warrantyCandidate?.name}, you will be removing ${selectedWarranty?.name}. Do you want to make the change?`}
        buttons={{ cancel: 'Cancel', confirm: 'Change' }}
      />
    </>
  );
};

export default SidePanelAddOnOptionsTab;
